import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, X, SlidersHorizontal } from "lucide-react";
import { Plan, SubscriptionStatus, Platform, SubscriptionCycle } from "@/types/enums";
import type { AdminSubscriptionFilters } from "@/types/subscription";
import { useDebounce } from "@/hooks/use-debounce";
import { cn } from "@/lib/utils";

interface SubscriptionFiltersProps {
  filters: AdminSubscriptionFilters;
  onFiltersChange: (filters: AdminSubscriptionFilters) => void;
}

const PLATFORM_NONE_VALUE = "__NONE__";

function getActiveFilterCount(filters: AdminSubscriptionFilters): number {
  let count = 0;
  if (filters.plan) count += 1;
  if (filters.status) count += 1;
  if (filters.platform || filters.platformIsNull) count += 1;
  if (filters.subscriptionCycle) count += 1;
  return count;
}

function getPlatformSelectValue(filters: AdminSubscriptionFilters): string {
  if (filters.platformIsNull) return PLATFORM_NONE_VALUE;
  return filters.platform || "ALL";
}

export function SubscriptionFiltersComponent({ filters, onFiltersChange }: SubscriptionFiltersProps) {
  const [searchInput, setSearchInput] = useState(filters.searchQuery || "");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const debouncedSearch = useDebounce(searchInput, 300);

  useEffect(() => {
    const normalizedSearch = debouncedSearch || undefined;
    if (normalizedSearch !== filters.searchQuery) {
      onFiltersChange({
        ...filters,
        searchQuery: normalizedSearch,
      });
    }
  }, [debouncedSearch, filters, onFiltersChange]);

  const hasActiveFilters = Boolean(
    filters.searchQuery ||
      filters.plan ||
      filters.status ||
      filters.platform ||
      filters.platformIsNull ||
      filters.subscriptionCycle,
  );
  const activeFilterCount = getActiveFilterCount(filters);

  const clearFilters = () => {
    setSearchInput("");
    onFiltersChange({
      searchQuery: undefined,
      plan: undefined,
      status: undefined,
      platform: undefined,
      platformIsNull: undefined,
      subscriptionCycle: undefined,
    });
  };

  const filterSelects = (
    <>
      <Select
        value={filters.plan || "ALL"}
        onValueChange={(value) =>
          onFiltersChange({
            ...filters,
            plan: value === "ALL" ? undefined : (value as Plan),
          })
        }
      >
        <SelectTrigger className="w-full md:w-[180px]" aria-label="Filter by plan">
          <SelectValue placeholder="플랜 선택" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ALL">전체 플랜</SelectItem>
          <SelectItem value={Plan.FREE}>FREE</SelectItem>
          <SelectItem value={Plan.PRO}>PRO</SelectItem>
          <SelectItem value={Plan.PLUS}>PLUS</SelectItem>
          <SelectItem value={Plan.PREMIUM}>PREMIUM</SelectItem>
          <SelectItem value={Plan.STUDENT_PASS}>STUDENT</SelectItem>
          <SelectItem value={Plan.PRO_TRIAL}>TRIAL</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.status || "ALL"}
        onValueChange={(value) =>
          onFiltersChange({
            ...filters,
            status: value === "ALL" ? undefined : (value as SubscriptionStatus),
          })
        }
      >
        <SelectTrigger className="w-full md:w-[180px]" aria-label="Filter by status">
          <SelectValue placeholder="상태 선택" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ALL">전체 상태</SelectItem>
          <SelectItem value={SubscriptionStatus.ACTIVE}>활성</SelectItem>
          <SelectItem value={SubscriptionStatus.PENDING_PAYMENT}>결제 대기</SelectItem>
          <SelectItem value={SubscriptionStatus.PENDING_CANCELLATION}>해지 대기</SelectItem>
          <SelectItem value={SubscriptionStatus.PENDING_PLAN_CHANGE}>플랜 변경 대기</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={getPlatformSelectValue(filters)}
        onValueChange={(value) => {
          if (value === "ALL") {
            onFiltersChange({ ...filters, platform: undefined, platformIsNull: undefined });
            return;
          }
          if (value === PLATFORM_NONE_VALUE) {
            onFiltersChange({ ...filters, platform: undefined, platformIsNull: true });
            return;
          }
          onFiltersChange({ ...filters, platform: value as Platform, platformIsNull: undefined });
        }}
      >
        <SelectTrigger className="w-full md:w-[180px]" aria-label="Filter by platform">
          <SelectValue placeholder="플랫폼 선택" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ALL">전체 플랫폼</SelectItem>
          <SelectItem value={Platform.TOSS}>TOSS</SelectItem>
          <SelectItem value={Platform.IOS}>IOS</SelectItem>
          <SelectItem value={Platform.ANDROID}>ANDROID</SelectItem>
          <SelectItem value={Platform.STRIPE}>STRIPE</SelectItem>
          <SelectItem value={PLATFORM_NONE_VALUE}>없음 (null)</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.subscriptionCycle || "ALL"}
        onValueChange={(value) =>
          onFiltersChange({
            ...filters,
            subscriptionCycle:
              value === "ALL" ? undefined : (value as SubscriptionCycle),
          })
        }
      >
        <SelectTrigger className="w-full md:w-[160px]" aria-label="Filter by cycle">
          <SelectValue placeholder="주기 선택" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ALL">전체 주기</SelectItem>
          <SelectItem value={SubscriptionCycle.MONTHLY}>월간</SelectItem>
          <SelectItem value={SubscriptionCycle.YEARLY}>연간</SelectItem>
        </SelectContent>
      </Select>

      {hasActiveFilters && (
        <Button variant="ghost" onClick={clearFilters} className="w-full md:w-auto min-h-[44px] md:min-h-0" aria-label="Clear all filters">
          <X className="mr-2 h-4 w-4" />
          초기화
        </Button>
      )}
    </>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        {/* Search input - always visible */}
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder="이메일 또는 이름으로 검색..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9 h-11 md:h-9"
              aria-label="Search subscriptions by email or name"
            />
          </div>

          {/* Mobile filter toggle button */}
          <Button
            variant="outline"
            onClick={() => setMobileFiltersOpen((prev) => !prev)}
            className="md:hidden h-11 min-w-[44px] relative"
            aria-label="필터 토글"
            aria-expanded={mobileFiltersOpen}
          >
            <SlidersHorizontal className="h-4 w-4" />
            {activeFilterCount > 0 && (
              <Badge
                variant="destructive"
                className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
              >
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </div>

        {/* Desktop: filters always visible */}
        <div className="hidden md:contents">
          {filterSelects}
        </div>
      </div>

      {/* Mobile: collapsible filter panel */}
      <div
        className={cn(
          "md:hidden flex flex-col gap-3",
          !mobileFiltersOpen && "hidden"
        )}
      >
        {filterSelects}
      </div>

      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">필터:</span>
          {filters.searchQuery && (
            <Badge variant="secondary" className="gap-1 pr-1">
              검색: &quot;{filters.searchQuery}&quot;
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  onFiltersChange({ ...filters, searchQuery: undefined });
                }}
                className="ml-1 rounded-full p-0.5 hover:bg-muted"
                aria-label="검색 필터 제거"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.plan && (
            <Badge variant="secondary" className="gap-1 pr-1">
              플랜: {filters.plan}
              <button
                type="button"
                onClick={() => onFiltersChange({ ...filters, plan: undefined })}
                className="ml-1 rounded-full p-0.5 hover:bg-muted"
                aria-label="플랜 필터 제거"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.status && (
            <Badge variant="secondary" className="gap-1 pr-1">
              상태: {filters.status}
              <button
                type="button"
                onClick={() => onFiltersChange({ ...filters, status: undefined })}
                className="ml-1 rounded-full p-0.5 hover:bg-muted"
                aria-label="상태 필터 제거"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.platform && (
            <Badge variant="secondary" className="gap-1 pr-1">
              플랫폼: {filters.platform}
              <button
                type="button"
                onClick={() => onFiltersChange({ ...filters, platform: undefined })}
                className="ml-1 rounded-full p-0.5 hover:bg-muted"
                aria-label="플랫폼 필터 제거"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.platformIsNull && (
            <Badge variant="secondary" className="gap-1 pr-1">
              플랫폼: 없음
              <button
                type="button"
                onClick={() => onFiltersChange({ ...filters, platformIsNull: undefined })}
                className="ml-1 rounded-full p-0.5 hover:bg-muted"
                aria-label="플랫폼 필터 제거"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.subscriptionCycle && (
            <Badge variant="secondary" className="gap-1 pr-1">
              주기:{" "}
              {filters.subscriptionCycle === SubscriptionCycle.MONTHLY
                ? "월간"
                : "연간"}
              <button
                type="button"
                onClick={() =>
                  onFiltersChange({ ...filters, subscriptionCycle: undefined })
                }
                className="ml-1 rounded-full p-0.5 hover:bg-muted"
                aria-label="주기 필터 제거"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            전체 초기화
          </button>
        </div>
      )}
    </div>
  );
}
